import React, { useState, useRef, useEffect } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './EditableText.module.css';

// ── CVA definition ─────────────────────────────────────────────────────────

const inlineEditField = cva(styles.base, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
      xl: styles.xl,
    },
    disabled: { true: styles.disabled },
  },
  defaultVariants: {
    size: 'md',
  },
});

// ── Types ──────────────────────────────────────────────────────────────────

type EditableTextVariants = VariantProps<typeof inlineEditField>;

interface EditableTextProps extends Omit<EditableTextVariants, 'disabled'> {
  /** Current value */
  value: string;
  /** Called when editing commits (blur or Enter) */
  onChange: (value: string) => void;
  /** Called when editing is cancelled (Escape) */
  onCancel?: () => void;
  /**
   * Text shown in display mode when `value` is empty (e.g. "Click to add a
   * title…"). NOT a floating label and NOT a native input placeholder — this
   * is empty-state copy for the read-only display row. Renamed from the
   * legacy `placeholder` prop to make the semantics clearer.
   */
  emptyText?: string;
  /** Controlled editing state */
  editing?: boolean;
  /** Called when editing state changes (controlled mode) */
  onEditingChange?: (editing: boolean) => void;
  /** Custom display render (receives the current value) */
  renderDisplay?: (value: string) => React.ReactNode;
  /** Font size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Auto-focus the input when editing becomes true */
  autoFocus?: boolean;
  /** Disable all interaction */
  disabled?: boolean;
  /** Maximum input length */
  maxLength?: number;
  /** Additional CSS class */
  className?: string;
}

// ── EditableText ────────────────────────────────────────────────────────

/**
 * EditableText — click-to-edit text field that swaps a display label for
 * an input when editing. Supports both uncontrolled and controlled edit state.
 *
 * **Uncontrolled:** Internal `isEditing` state; click to activate.
 * **Controlled:** Pass `editing` + `onEditingChange`; parent drives state.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | string | — | Current value |
 * | onChange | (value) => void | — | Called on commit (blur/Enter) |
 * | onCancel | () => void | — | Called on Escape |
 * | emptyText | string | — | Display-mode copy shown when value is empty |
 * | editing | boolean | — | Controlled editing state |
 * | onEditingChange | (editing) => void | — | Controlled mode toggle |
 * | renderDisplay | (value) => ReactNode | — | Custom display renderer |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Font size |
 * | autoFocus | boolean | true | Focus input on edit activation |
 * | disabled | boolean | false | Prevents editing |
 * | maxLength | number | — | Maximum character count |
 *
 * @example
 * // Uncontrolled
 * <EditableText value={title} onChange={setTitle} emptyText="Untitled" />
 *
 * @example
 * // Controlled
 * <EditableText
 *   value={title}
 *   onChange={handleCommit}
 *   editing={isEditing}
 *   onEditingChange={setIsEditing}
 * />
 */
export function EditableText({
  value,
  onChange,
  onCancel,
  emptyText = '',
  editing: editingProp,
  onEditingChange,
  renderDisplay,
  size = 'md',
  autoFocus = true,
  disabled = false,
  maxLength,
  className,
}: EditableTextProps) {
  const isControlled = editingProp !== undefined;
  const [internalEditing, setInternalEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  // Ref tracks editing state synchronously so the useEffect never reads a
  // stale isEditing value during concurrent React rendering. This prevents
  // a race where a Zustand store update (entity sync event) causes a re-render
  // with a new `value` before the editing state flip has committed, which
  // would otherwise reset the user's typed text.
  const isEditingRef = useRef(false);

  const isEditing = isControlled ? editingProp : internalEditing;
  isEditingRef.current = isEditing;

  // Keep input value in sync with external value when not editing.
  // Uses isEditingRef (not isEditing from the effect closure) to guard against
  // the concurrent-mode race described above.
  useEffect(() => {
    if (!isEditingRef.current) {
      setInputValue(value);
    }
  }, [value]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && autoFocus) {
      inputRef.current?.focus();
      // Move cursor to end
      const len = inputRef.current?.value.length ?? 0;
      inputRef.current?.setSelectionRange(len, len);
    }
  }, [isEditing, autoFocus]);

  const enterEditing = () => {
    if (disabled) return;
    setInputValue(value);
    if (isControlled) {
      onEditingChange?.(true);
    } else {
      setInternalEditing(true);
    }
  };

  const commit = (val: string) => {
    if (isControlled) {
      onEditingChange?.(false);
    } else {
      setInternalEditing(false);
    }
    onChange(val);
  };

  const cancel = () => {
    setInputValue(value);
    if (isControlled) {
      onEditingChange?.(false);
    } else {
      setInternalEditing(false);
    }
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(inputValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  const handleBlur = () => {
    commit(inputValue);
  };

  if (isEditing) {
    return (
      <span className={inlineEditField({ size, disabled, className })}>
        <input
          ref={inputRef}
          className={styles.input}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          maxLength={maxLength}
          data-testid="inline-edit-input"
        />
      </span>
    );
  }

  const displayContent = renderDisplay
    ? renderDisplay(value)
    : (value || (
        <span className={styles.displayEmpty}>{emptyText}</span>
      ));

  return (
    <span className={inlineEditField({ size, disabled, className })}>
      <span
        className={styles.display}
        onClick={enterEditing}
        role="button"
        tabIndex={disabled ? undefined : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            enterEditing();
          }
        }}
      >
        {displayContent}
      </span>
    </span>
  );
}
